import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

import { uploadListFile } from 'store/entities/submissions/thunks';
import { setFileUploaderState } from 'store/entities/submissions';

import useSearchSubmissions from 'utils/hooks/submissions/useSearchSubmissions';
import { formatFileSize, resolveFileTypeAcceptedExtensions } from 'utils/fileUtils';
import { notifyError } from 'utils/notifications';
import { formatDateTime } from 'utils/dateUtils';

import Button from 'components/UIComponents/Button';
import { Column, Row, Section } from 'components/UIComponents/Layout';
import Modal, { ModalCard } from 'components/UIComponents/Modal';
import LoadingSpinner from 'components/UIComponents/LoadingSpinner';

export default () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { fileUploaderState, listFileUploading } = useSelector(
    state => state.submissions
  );

  const {
    submissionId,
    tableName,
    fileType,
    fileUploaderOpen,
    dueDate,
  } = fileUploaderState;

  const accept = resolveFileTypeAcceptedExtensions(fileType);
  const doSearch = useSearchSubmissions();

  const {
    acceptedFiles,
    fileRejections,
    getRootProps,
    getInputProps,
  } = useDropzone({
    maxFiles: 1,
    disabled: listFileUploading,
    accept,
  });

  const resetUploader = useCallback(() => {
    acceptedFiles.splice(0, 1);
    fileRejections.splice(0, 1);
  }, [acceptedFiles, fileRejections]);

  const closeUploader = () =>
    dispatch(setFileUploaderState({ fileUploaderOpen: false }));

  const prepareFile = () => {
    const file = acceptedFiles[0];
    const fileName = file.name.replace(/\.[^/.]+$/, '');
    const namesMatch = fileName === tableName;
    let payload = new FormData();
    payload.append('file', file);

    if (!namesMatch) {
      window.confirm(
        t('datagate.external.submission.fileUpload.nameCheck', {
          tableName,
          fileName,
          dueDate: formatDateTime(dueDate),
        })
      ) && upload(payload);
    } else upload(payload);
  };

  const upload = async payload => {
    const uploadResult = await dispatch(
      uploadListFile({ submissionId, payload })
    );

    if (uploadListFile.fulfilled.match(uploadResult)) {
      closeUploader();
      doSearch();
    }
  };

  useEffect(() => {
    if (fileRejections.length > 0 && fileType)
      notifyError(
        t('datagate.external.submission.fileUpload.message.fileTypeError', {
          accept,
        })
      );
  }, [fileRejections, accept, fileType, t]);

  useEffect(() => {
    if (!fileUploaderOpen) {
      resetUploader();
    }
  }, [fileUploaderOpen, resetUploader]);

  const hasFile = acceptedFiles.length > 0;

  return (
    <Modal
      show={fileUploaderOpen}
      onClose={closeUploader}
      closeOnBlur={!listFileUploading}
    >
      <ModalCard>
        <LoadingSpinner
          loading={listFileUploading}
          message={t(
            'datagate.external.submission.fileUpload.message.fileUploading'
          )}
        />
        <ModalCard.Head>
          <ModalCard.Title>
            {t('datagate.external.submission.fileUpload.title')}
          </ModalCard.Title>
        </ModalCard.Head>
        <ModalCard.Body>
          <Section className="vertical-padding-md">
            <Row>
              <Column>
                {t('datagate.external.submission.fileUpload.title')}{' '}
                {`${tableName}.${fileType}`}
              </Column>
            </Row>
            <Row>
              <Column>
                <div {...getRootProps({ className: 'dropzone' })}>
                  <input {...getInputProps()} />
                  <p>
                    {t(
                      'datagate.external.submission.fileUpload.message.uploadBox'
                    )}
                  </p>
                </div>
              </Column>
            </Row>
            {hasFile && (
              <Row>
                <Column>
                  <h4>
                    {t('datagate.external.submission.fileUpload.label.file')}
                  </h4>
                  <ul>
                    {acceptedFiles.map(file => (
                      <li key={file.path}>
                        {file.path} - {formatFileSize(file.size)}
                      </li>
                    ))}
                  </ul>
                </Column>
              </Row>
            )}
          </Section>
        </ModalCard.Body>
        <ModalCard.Foot>
          <Button
            color="primary"
            type="button"
            disabled={listFileUploading}
            onClick={closeUploader}
            outlined
            id="closeUploaderButton"
          >
            &#215; {t('datagate.external.submission.fileUpload.button.cancel')}
          </Button>
          {hasFile && (
            <Button
              type="button"
              color="primary"
              disabled={listFileUploading}
              onClick={prepareFile}
              id="confirmUploaderButton"
            >
              {t('datagate.external.submission.fileUpload.button.upload')}
            </Button>
          )}
        </ModalCard.Foot>
      </ModalCard>
    </Modal>
  );
};
